import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useBreadcrumb } from "./useBreadcrumb";
import useMenuItems from "./useMenuItems";

export interface TrailEntry { path: string; label: string; }

const STORAGE_KEY = "v-nav-trail";
const MAX_DEPTH = 8;

function readStoredTrail(): TrailEntry[] {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is TrailEntry => e && typeof e.path === "string" && typeof e.label === "string"
    );
  } catch {
    return [];
  }
}

function writeStoredTrail(trail: TrailEntry[]) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trail));
  } catch {
    // sessionStorage unavailable (private mode / quota) — trail just won't persist across refresh.
  }
}

// Turns "/reports/ivr-campaigns" -> "IVR Campaign Reports"-ish fallback:
// title-cases the last non-numeric/non-guid path segment.
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_RE = /^\d+$/;

function humanizeLabel(path: string): string {
  const segments = path.split("/").filter(Boolean);
  let seg = segments[segments.length - 1];
  // Walk back past purely numeric/GUID segments (detail-page IDs) to find a meaningful word.
  let i = segments.length - 1;
  while (i >= 0 && (NUMERIC_RE.test(segments[i]) || GUID_RE.test(segments[i]))) {
    i--;
  }
  seg = i >= 0 ? segments[i] : (segments[segments.length - 1] ?? "");
  if (!seg) return "Home";
  return seg
    .split(/[-_]/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Maintains a real back-stack of visited pages (sessionStorage-backed, per-tab).
 * Used to power an always-accurate breadcrumb/back-link, replacing the
 * menu-only useBreadcrumb() which can't see detail/report sub-pages that
 * aren't in the sidebar tree.
 */
export function useNavigationTrail() {
  const location = useLocation();
  const navigate = useNavigate();
  const menuBreadcrumb = useBreadcrumb();
  const menuItems = useMenuItems();

  const [trail, setTrail] = useState<TrailEntry[]>(() => readStoredTrail());
  const lastPathRef = useRef<string | null>(null);

  const friendlyLabel = useMemo(() => {
    const path = location.pathname;
    // Prefer the menu-matched label (most specific sublink label) when available.
    const menuMatch = menuBreadcrumb[menuBreadcrumb.length - 1];
    if (menuMatch && menuMatch.to && (path === menuMatch.to || path.startsWith(menuMatch.to + "/"))) {
      return menuMatch.label;
    }
    return humanizeLabel(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, menuItems]);

  useEffect(() => {
    const path = location.pathname;
    if (lastPathRef.current === path) return; // query-string-only change on same path — no new entry
    lastPathRef.current = path;

    setTrail(prev => {
      const existingIdx = prev.findIndex(e => e.path === path);
      let next: TrailEntry[];
      if (existingIdx >= 0) {
        // Revisiting an earlier entry (crumb click / browser back) — truncate forward from there.
        next = prev.slice(0, existingIdx + 1);
        // Refresh label in case menu context changed.
        next[next.length - 1] = { path, label: friendlyLabel };
      } else if (prev.length === 0) {
        // Fresh tab / direct load / refresh — sessionStorage has no history yet. Seed with the
        // menu-derived parent crumb(s) (if any) so a bookmarked/deep-linked page still shows
        // "Section > Page" instead of a bare, back-link-less page on first paint.
        const seed: TrailEntry[] = menuBreadcrumb
          .filter(c => c.to && c.to !== path)
          .map(c => ({ path: c.to as string, label: c.label }));
        next = [...seed, { path, label: friendlyLabel }];
      } else {
        next = [...prev, { path, label: friendlyLabel }];
        if (next.length > MAX_DEPTH) {
          next = next.slice(next.length - MAX_DEPTH);
        }
      }
      writeStoredTrail(next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, friendlyLabel]);

  function goBack() {
    if (trail.length > 1) {
      const prevEntry = trail[trail.length - 2];
      navigate(prevEntry.path);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/");
  }

  return {
    trail,
    goBack,
    current: trail[trail.length - 1],
  };
}
